import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, ChevronLeft, Globe, Smartphone, Megaphone, Cloud, Wrench, Layout, Banknote, Clock, User, Mail, Phone, CheckCircle2, Sparkles, ShieldCheck, Cpu, Monitor, Code2 } from 'lucide-react';
import toast from 'react-hot-toast';

const Quote = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Web Development',
    budget: '',
    timeline: '',
    message: '',
    // Detailed Requirements
    subType: '', // e.g., Static, Dynamic, E-commerce
    needsDomain: false,
    needsHosting: false,
    needsEmail: false,
    platform: [] as string[], // for mobile
    features: [] as string[]
  });

  const services = [
    { 
      name: 'Web Development', 
      icon: Globe, 
      desc: 'Portals, E-commerce & Web Apps', 
      color: 'bg-blue-500',
      types: ['Static Site', 'Dynamic (CMS)', 'E-commerce', 'SaaS Application'],
      features: ['Payment Gateway', 'User Authentication', 'SEO Setup', 'Multi-language', 'Blog Section']
    },
    { 
      name: 'Mobile Development', 
      icon: Smartphone, 
      desc: 'iOS, Android & Hybrid Apps', 
      color: 'bg-pink-500',
      types: ['Consumer App', 'Enterprise App', 'Gaming App', 'Utility App'],
      features: ['Push Notifications', 'Real-time Chat', 'Geolocation', 'Offline Mode', 'Social Login']
    },
    { 
      name: 'Digital Marketing', 
      icon: Megaphone, 
      desc: 'SEO, SEM & Social Growth', 
      color: 'bg-emerald-500',
      types: ['Full Digital Package', 'SEO Only', 'SMM Only', 'PPC Management'],
      features: ['Keyword Research', 'Content Creation', 'Ad Campaign Setup', 'Monthly Reporting', 'Lead Generation']
    },
    { 
      name: 'Cloud Solutions', 
      icon: Cloud, 
      desc: 'AWS, Azure & Migration', 
      color: 'bg-amber-500',
      types: ['AWS Infrastructure', 'Google Cloud', 'Azure Setup', 'Private Cloud'],
      features: ['Serverless Setup', 'Auto-scaling', 'Backup & Recovery', 'Security Audit', 'Cost Optimization']
    },
    { 
      name: 'Maintenance & Support', 
      icon: Wrench, 
      desc: '24/7 Support & Security', 
      color: 'bg-slate-700',
      types: ['Standard Monthly', 'Premium 24/7', 'Security Focused', 'On-call Support'],
      features: ['Uptime Monitoring', 'Bug Fixing', 'Security Updates', 'Performance Audit', 'Content Updates']
    },
    { 
      name: 'UI/UX Design', 
      icon: Layout, 
      desc: 'User Research & Modern UI', 
      color: 'bg-violet-500',
      types: ['Full Product Design', 'Landing Page', 'Mobile UI', 'Re-design'],
      features: ['Prototyping', 'User Interviews', 'Design System', 'Usability Testing', 'Wireframing']
    }
  ];

  const budgetOptions: Record<string, string[]> = {
    'Less than 1 Month': ['Under ₹15,000', '₹15,000 - ₹30,000', '₹30,000+'],
    '1-3 Months': ['₹30,000 - ₹75,000', '₹75,000 - ₹1,50,000', '₹1,50,000+'],
    '3-6 Months': ['₹1,50,000 - ₹5,00,000', '₹5,00,000 - ₹10,00,000', '₹10,00,000+'],
    '6+ Months': ['₹10,00,000 - ₹25,00,000', '₹25,00,000+', 'Contact for Enterprise']
  };

  const timelines = ['Less than 1 Month', '1-3 Months', '3-6 Months', '6+ Months'];

  const nextStep = () => {
    if (step === 2) {
      if (!formData.subType) {
        toast.error('Please select a project type.');
        return;
      }
    }
    setStep(prev => prev + 1);
  };
  const prevStep = () => setStep(prev => prev - 1);

  const toggleFeature = (feat: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feat) 
        ? prev.features.filter(f => f !== feat) 
        : [...prev.features, feat]
    }));
  };

  // file change handler intentionally omitted; file input is sent directly in form

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.budget) {
      toast.error('Please select a budget range based on your timeline.');
      return;
    }
    setIsSubmitting(true);
    const loadingToast = toast.loading('Engineering your custom roadmap...', {
      style: { borderRadius: '10px', background: '#4F46E5', color: '#fff' },
    });

    const submissionData = new FormData();
    submissionData.append("access_key", "9fcd84f7-aceb-44de-9438-ab9a40a73fb7");
    submissionData.append("subject", `Full Quote Request: ${formData.service} (${formData.subType})`);
    submissionData.append("from_name", "OETechs Project Architect");
    
    Object.entries(formData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        submissionData.append(key, value.join(', '));
      } else {
        submissionData.append(key, String(value));
      }
    });

    if (fileInputRef.current?.files?.[0]) {
      submissionData.append("attachment", fileInputRef.current.files[0]);
    }

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: submissionData
      });

      const data = await response.json();

      if (data.success) {
        toast.dismiss(loadingToast);
        toast.success('Your technical brief is in our hands!', {
          icon: '🚀',
          style: { borderRadius: '10px', background: '#4F46E5', color: '#fff' },
        });
        setIsSubmitted(true);
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (error: any) {
      toast.dismiss(loadingToast);
      toast.error(error.message || 'Error processing request.', {
        style: { borderRadius: '10px', background: '#4F46E5', color: '#fff' },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentService = services.find(s => s.name === formData.service);

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#f8fafc] dark:bg-[#001226] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="max-w-2xl w-full bg-white dark:bg-[#001a35] p-10 md:p-16 rounded-[4rem] text-center shadow-2xl border border-slate-100 dark:border-white/10 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-3 bg-gradient-to-r from-[#002a5c] via-indigo-400 to-emerald-500" />
          <div className="w-24 h-24 bg-blue-600 text-white rounded-3xl flex items-center justify-center mx-auto mb-10 shadow-xl rotate-12">
            <CheckCircle2 size={48} />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-[#4F46E5] dark:text-white mb-6">Technical Brief Sent!</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-10 text-lg leading-relaxed">
            Our technical architects are reviewing your {formData.service} requirements. We will prepare a detailed effort estimation and project roadmap for you within 24 hours.
          </p>
          <button 
            onClick={() => window.location.href = '/'}
            className="w-full py-5 bg-[#4F46E5] dark:bg-blue-600 text-white rounded-2xl font-semibold hover:scale-[1.02] transition-all shadow-xl shadow-blue-500/20"
          >
            Return to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-[#f8fafc] dark:bg-[#001226] min-h-screen transition-colors duration-500 pb-24">
      {/* Header */}
      <section className="bg-white dark:bg-[#001a35] border-b border-slate-100 dark:border-white/5 py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full mb-8 text-xs font-black uppercase tracking-[0.25em]">
              <Sparkles size={14} className="animate-pulse" />
              <span>Deep Analysis Quote</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-[#4F46E5] dark:text-white mb-6 tracking-tight leading-tight">
              Engineer Your <span className="text-blue-600">Digital Asset</span>
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              Step into our technical workshop. Provide the details, and we'll engineer the perfect solution for your business scale.
            </p>
          </motion.div>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 blur-[100px] -mr-48 -mt-48" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/5 blur-[100px] -ml-48 -mb-48" />
      </section>

      <section className="max-w-5xl mx-auto px-4 -mt-12 relative z-20">
        <div className="bg-white/90 dark:bg-[#001a35]/90 backdrop-blur-3xl rounded-[3.5rem] shadow-2xl border border-white dark:border-white/10 overflow-hidden">
          
          {/* Enhanced Progress */}
          <div className="bg-slate-50/50 dark:bg-white/5 border-b border-slate-100 dark:border-white/5 px-8 md:px-12 py-8">
            <div className="flex items-center justify-between mb-6">
               {[1, 2, 3, 4].map((s) => (
                 <div key={s} className="flex items-center gap-3">
                   <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-semibold transition-all duration-500 ${
                     step >= s ? 'bg-blue-600 text-white shadow-lg scale-110' : 'bg-white dark:bg-white/10 text-slate-300 dark:text-slate-600 border border-slate-100 dark:border-white/10'
                   }`}>
                     {s}
                   </div>
                   {step === s && (
                     <motion.span 
                       initial={{ opacity: 0, x: -10 }} 
                       animate={{ opacity: 1, x: 0 }}
                       className="text-[10px] font-black uppercase tracking-[0.2em] text-[#4F46E5] dark:text-white hidden md:block"
                     >
                       {s === 1 ? 'Service' : s === 2 ? 'Analysis' : s === 3 ? 'Timeline' : 'Review'}
                     </motion.span>
                   )}
                 </div>
               ))}
            </div>
            <div className="h-1.5 w-full bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
               <motion.div className="h-full bg-blue-600" animate={{ width: `${(step / 4) * 100}%` }} />
            </div>
          </div>

          <div className="p-8 md:p-16 min-h-[500px]">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="space-y-12">
                  <div className="text-center">
                    <h3 className="text-3xl font-semibold text-[#4F46E5] dark:text-white mb-2">Primary Expertise</h3>
                    <p className="text-slate-500 dark:text-slate-400">Choose the digital discipline for your project.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {services.map((s) => (
                      <div 
                        key={s.name}
                        onClick={() => { setFormData(prev => ({ ...prev, service: s.name, specifics: [], subType: '', features: [] })); nextStep(); }}
                        className={`group p-8 rounded-[3rem] border-2 transition-all cursor-pointer relative overflow-hidden flex flex-col items-center text-center ${
                          formData.service === s.name ? 'border-blue-600 bg-blue-50/30 dark:bg-blue-900/10' : 'border-slate-50 dark:border-white/5 bg-white dark:bg-[#001a35]'
                        }`}
                      >
                        <div className={`w-16 h-16 rounded-2xl ${s.color} text-white flex items-center justify-center mb-6 shadow-xl group-hover:rotate-6 transition-all duration-500`}>
                          <s.icon size={32} />
                        </div>
                        <h4 className="text-lg font-semibold text-[#4F46E5] dark:text-white mb-2">{s.name}</h4>
                        <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest leading-relaxed">{s.desc}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-12">
                  <div className="text-center">
                    <h3 className="text-3xl font-semibold text-[#4F46E5] dark:text-white mb-2">Requirement Deep-Dive</h3>
                    <p className="text-slate-500 dark:text-slate-400">Define the core architecture of your {formData.service} project.</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div className="space-y-8">
                       <div className="space-y-4">
                          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 ml-1">Project Type</label>
                          <div className="grid grid-cols-1 gap-2">
                             {currentService?.types.map(t => (
                               <button 
                                 key={t}
                                 onClick={() => setFormData(p => ({ ...p, subType: t }))}
                                 className={`p-4 rounded-2xl border-2 text-left text-sm font-semibold transition-all ${
                                   formData.subType === t ? 'bg-[#4F46E5] border-[#4F46E5] text-white' : 'bg-slate-50 dark:bg-white/5 border-transparent text-slate-500'
                                 }`}
                               >
                                 {t}
                               </button>
                             ))}
                          </div>
                       </div>

                       {formData.service === 'Web Development' && (
                         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                            {[
                              { label: 'Domain Required', name: 'needsDomain', icon: Globe },
                              { label: 'Hosting Setup', name: 'needsHosting', icon: Monitor },
                              { label: 'Official Email', name: 'needsEmail', icon: Mail }
                            ].map(item => (
                              <label key={item.name} className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-white/5 border border-transparent hover:border-blue-300 cursor-pointer transition-all">
                                 <input 
                                   type="checkbox" 
                                   name={item.name} 
                                   checked={(formData as any)[item.name]} 
                                   onChange={handleChange}
                                   className="w-5 h-5 rounded-lg accent-blue-600"
                                 />
                                 <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-[#4F46E5] dark:text-white">{item.label}</span>
                                    <item.icon size={14} className="text-blue-500 mt-1" />
                                 </div>
                              </label>
                            ))}
                         </div>
                       )}
                    </div>

                    <div className="space-y-6">
                       <label className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 ml-1">Key Modules / Features</label>
                       <div className="grid grid-cols-1 gap-3">
                          {currentService?.features.map(f => (
                            <div 
                              key={f}
                              onClick={() => toggleFeature(f)}
                              className={`p-5 rounded-[1.5rem] border-2 cursor-pointer flex items-center justify-between transition-all ${
                                formData.features.includes(f) ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-white dark:bg-white/5 border-slate-50 dark:border-white/5 text-slate-500'
                              }`}
                            >
                               <span className="text-sm font-semibold">{f}</span>
                               {formData.features.includes(f) && <CheckCircle2 size={16} />}
                            </div>
                          ))}
                       </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-12">
                    <button onClick={prevStep} className="text-slate-400 font-semibold hover:text-blue-600 transition-colors flex items-center gap-2">
                      <ChevronLeft size={20} /> Back
                    </button>
                    <button onClick={nextStep} className="px-12 py-5 bg-blue-600 text-white rounded-2xl font-semibold shadow-xl shadow-blue-500/20 hover:scale-105 transition-transform">
                      Continue to Timeline
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-12">
                  <div className="text-center">
                    <h3 className="text-3xl font-semibold text-[#4F46E5] dark:text-white mb-2">Delivery & Investment</h3>
                    <p className="text-slate-500 dark:text-slate-400">Specify your launch targets and investment capacity.</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                     <div className="space-y-6">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 ml-1 flex items-center gap-2">
                           <Clock size={14} /> 1. Deployment Timeline
                        </label>
                        <div className="grid grid-cols-1 gap-3">
                           {timelines.map(t => (
                             <button 
                               key={t}
                               onClick={() => setFormData(p => ({ ...p, timeline: t, budget: '' }))}
                               className={`p-5 rounded-2xl border-2 text-left font-semibold transition-all ${
                                 formData.timeline === t ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-slate-50 dark:bg-white/5 border-transparent text-slate-500'
                               }`}
                             >
                               {t}
                             </button>
                           ))}
                        </div>
                     </div>

                     <div className="space-y-6">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 ml-1 flex items-center gap-2">
                           <Banknote size={14} /> 2. Investment Capacity (INR)
                        </label>
                        {!formData.timeline ? (
                          <div className="p-10 rounded-3xl border-2 border-dashed border-slate-200 dark:border-white/10 flex flex-col items-center justify-center text-center">
                             <Clock size={32} className="text-slate-200 mb-4" />
                             <p className="text-xs text-slate-400 font-semibold">Please select a timeline first to see budget options.</p>
                          </div>
                        ) : (
                          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 gap-3">
                             {budgetOptions[formData.timeline].map(b => (
                               <button 
                                 key={b}
                                 onClick={() => setFormData(p => ({ ...p, budget: b }))}
                                 className={`p-5 rounded-2xl border-2 text-left text-sm font-semibold transition-all ${
                                   formData.budget === b ? 'bg-[#4F46E5] border-[#4F46E5] text-white shadow-lg' : 'bg-white dark:bg-white/5 border-slate-100 dark:border-white/5 text-slate-500 hover:border-blue-200'
                                 }`}
                               >
                                 {b}
                               </button>
                             ))}
                          </motion.div>
                        )}
                     </div>
                  </div>

                  <div className="flex items-center justify-between pt-12">
                    <button onClick={prevStep} className="text-slate-400 font-semibold hover:text-blue-600 transition-colors flex items-center gap-2">
                      <ChevronLeft size={20} /> Back
                    </button>
                    <button 
                      onClick={nextStep} 
                      disabled={!formData.timeline || !formData.budget}
                      className="px-12 py-5 bg-blue-600 text-white rounded-2xl font-semibold shadow-xl shadow-blue-500/20 hover:scale-105 transition-transform disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed"
                    >
                      Continue to Review
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 4 && (
                <motion.div key="s4" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-12">
                  <div className="text-center">
                    <h3 className="text-3xl font-semibold text-[#4F46E5] dark:text-white mb-2">Final Validation</h3>
                    <p className="text-slate-500 dark:text-slate-400">Complete your profile to receive the technical proposal.</p>
                  </div>

                  <div className="max-w-2xl mx-auto space-y-8">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                           <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Your Identity</label>
                           <div className="relative group">
                              <User size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-blue-500" />
                              <input required name="name" value={formData.name} onChange={handleChange} type="text" placeholder="Full Name" className="w-full bg-slate-50 dark:bg-white/5 border-2 border-transparent focus:border-blue-500 rounded-2xl py-5 pl-14 pr-6 text-sm outline-none transition-all" />
                           </div>
                        </div>
                        <div className="space-y-2">
                           <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Work Email</label>
                           <div className="relative group">
                              <Mail size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-blue-500" />
                              <input required name="email" value={formData.email} onChange={handleChange} type="email" placeholder="Email Address" className="w-full bg-slate-50 dark:bg-white/5 border-2 border-transparent focus:border-blue-500 rounded-2xl py-5 pl-14 pr-6 text-sm outline-none transition-all" />
                           </div>
                        </div>
                     </div>

                     <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Direct Phone</label>
                        <div className="relative group">
                           <Phone size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-blue-500" />
                           <input required name="phone" value={formData.phone} onChange={handleChange} type="tel" placeholder="+91 XXXX XXX XXX" className="w-full bg-slate-50 dark:bg-white/5 border-2 border-transparent focus:border-blue-500 rounded-2xl py-5 pl-14 pr-6 text-sm outline-none transition-all" />
                        </div>
                     </div>

                     <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Brief Description (Optional)</label>
                        <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Any specific notes for our architects?" rows={4} className="w-full bg-slate-50 dark:bg-white/5 border-2 border-transparent focus:border-blue-500 rounded-[2.5rem] p-8 text-sm outline-none transition-all resize-none" />
                     </div>

                     <div className="flex flex-col gap-4">
                        <button 
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          className="w-full py-6 bg-blue-600 text-white rounded-3xl font-black text-lg shadow-2xl shadow-blue-500/30 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-4"
                        >
                           {isSubmitting ? <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin" /> : <>Generate Technical Quote <Send size={24} /></>}
                        </button>
                        <button onClick={prevStep} className="py-4 text-slate-400 font-semibold hover:text-blue-600 transition-colors">
                           Refine Logic
                        </button>
                     </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="bg-slate-50/50 dark:bg-white/5 border-t border-slate-100 dark:border-white/5 p-12">
             <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {[
                  { icon: ShieldCheck, title: 'Data Encryption', desc: 'Secure brief handling' },
                  { icon: Cpu, title: 'AI Optimized', desc: 'Precision estimation' },
                  { icon: Monitor, title: 'Cross-Review', desc: 'Expert technical audit' },
                  { icon: Code2, title: 'Clean Architecture', desc: 'Industry standards' }
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center text-center gap-3">
                    <div className="w-12 h-12 bg-white dark:bg-white/10 rounded-2xl shadow-sm flex items-center justify-center text-blue-600">
                       <item.icon size={24} />
                    </div>
                    <div>
                       <h5 className="text-[10px] font-black uppercase tracking-widest text-[#4F46E5] dark:text-white mb-1">{item.title}</h5>
                       <p className="text-[9px] text-slate-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
             </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quote;
